/**
 * PublicFeedbackButton — Feedback FAB for unauthenticated / public pages.
 *
 * Only renders when the user is NOT logged in. Feedback submission requires
 * authentication (the UITL intake is auth-gated), so instead of a broken
 * anonymous submit this invites the visitor to log in. Authenticated pages use
 * FeedbackButton inside the main app layout.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Fab, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
} from '@mui/material';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';

const PublicFeedbackButton: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Only for logged-out visitors; FeedbackButton handles authenticated users.
  const isLoggedIn = !!localStorage.getItem('access_token');
  if (isLoggedIn) return null;

  return (
    <>
      <Fab
        aria-label="Submit feedback"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
          bgcolor: '#D4A843', color: '#fff', '&:hover': { bgcolor: '#C49A3A' },
        }}
      >
        <FeedbackOutlinedIcon />
      </Fab>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Send Feedback</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Log in to send feedback and track its progress — a real person reads,
            tests, and acts on every submission.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Not now</Button>
          <Button
            variant="contained"
            onClick={() => { setOpen(false); navigate('/login'); }}
            sx={{ bgcolor: '#D4A843', '&:hover': { bgcolor: '#C49A3A' } }}
          >
            Log in
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PublicFeedbackButton;
